import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import { useState } from "react";
import "./styles.css";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ChallengeTiersItem {
    _id: string;
    name: string;
}

export interface ChallengeTiersPopupDetails {
    initialSelected?: ChallengeTiersItem[];
    onSave: (tiers: ChallengeTiersItem[]) => void;
}

interface Props {
    onClose: () => void;
    details?: ChallengeTiersPopupDetails;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ChallengeTiersPopup({ onClose, details }: Props) {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];

    const [selected, setSelected] = useState<ChallengeTiersItem[]>(details?.initialSelected ?? []);
    const [search, setSearch] = useState("");

    const { data, loading } = useFetch("challenges/tiers");
    const tiers: ChallengeTiersItem[] = data?.data?.tiers ?? [];
    const filteredTiers = search.trim() ? tiers.filter((tier) => tier.name.toLowerCase().includes(search.trim().toLowerCase())) : tiers;

    const isSelected = (id: string) => selected.some((s) => s._id === id);

    const toggleTier = (tier: ChallengeTiersItem) => {
        setSelected((prev) => (isSelected(tier._id) ? prev.filter((s) => s._id !== tier._id) : [...prev, tier]));
    };

    const handleSave = () => {
        details?.onSave(selected);
        onClose();
    };

    return (
        <div className="challenge-tiers-popup">
            <div className="ctip-header">
                <h2 className="ctip-title">{t.challengeForm.tiersSection}</h2>
                <button type="button" className="ctip-close" onClick={onClose} aria-label="Close">
                    ×
                </button>
            </div>

            <div className="ctip-search">
                <input
                    className="ctip-search-input"
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t.actionsBar.search}
                    autoFocus
                />
            </div>

            <div className="ctip-body">
                {loading ? (
                    <div className="ctiip-loading">
                        <span className="ctiip-spinner" />
                    </div>
                ) : filteredTiers.length === 0 ? (
                    <div className="ctiip-empty">{t.table.empty}</div>
                ) : (
                    <div className="ctiip-grid">
                        {filteredTiers.map((tier) => (
                            <button
                                key={tier._id}
                                type="button"
                                className={`ctiip-card${isSelected(tier._id) ? " ctiip-card--selected" : ""}`}
                                onClick={() => toggleTier(tier)}
                            >
                                <span className="ctiip-card-name">{tier.name}</span>
                                {isSelected(tier._id) && <span className="ctiip-card-check">✓</span>}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="ctiip-footer">
                <button type="button" className="ctiip-btn ctiip-btn--cancel" onClick={onClose}>
                    {t.challengeForm.cancel}
                </button>
                <button type="button" className="ctiip-btn ctiip-btn--save" onClick={handleSave}>
                    {selected.length > 0 ? `${t.challengeForm.choose} (${selected.length})` : t.challengeForm.choose}
                </button>
            </div>
        </div>
    );
}
